/**
 * Tariff-specific hooks using TanStack Query
 * Provides data fetching, caching, and mutations for tariff operations
 */

import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useAuth } from '@clerk/clerk-react';
import { useAuthContext } from '@/contexts/AuthContext';
import { TariffsService, OpenAPI } from '@/api';
import type {
  billing_CreateTariffVersionRequest,
  billing_BulkUpdateTariffRatesRequest,
  billing_TariffRateRequest,
  billing_TariffRateResponse,
  billing_TariffVersionResponse,
} from '@/api';
// Value import: the request's `status` enum is used as a value below.
import { billing_UpdateTariffVersionRequest } from '@/api';
// Value import: the request's `age_type` enum is used as a value below.
import { billing_TariffRateRequest as billing_TariffRateRequestEnums } from '@/api/models/billing_TariffRateRequest';
import { toEnumMember } from '@/lib/enumMember';
import { toast } from 'sonner';
import { logger } from '@/lib/sentry';
import { useLingui } from '@lingui/react';
import { msg } from '@lingui/macro';
import { TariffTransformer } from '@/lib/fieldTransformers';
import { useModuleGate } from '@/hooks/useAccess';
import { TARIFF_VERSION_LIST_READ_MODULES } from '@/lib/modules';
import { usePaginatedQuery, type PaginatedResponse } from './usePagination';
import type { TariffVersion } from '@/types/billing';
import { apiErrorFromResponse } from '@/hooks/billing/fetchApiError';
import { retryUnlessClientError } from '@/lib/queryRetry';
import { errorStatus, errorToastText } from '@/lib/translateBackendError';

// Query keys for tariffs
export const tariffKeys = {
  all: ['tariffs'] as const,
  lists: () => [...tariffKeys.all, 'list'] as const,
  list: (filters: Record<string, string | number | boolean>) => [...tariffKeys.lists(), { filters }] as const,
  listPaginated: (params: { limit: number; offset: number }) => [...tariffKeys.lists(), 'paginated', params] as const,
  details: () => [...tariffKeys.all, 'detail'] as const,
  detail: (id: string) => [...tariffKeys.details(), id] as const,
  rates: (versionId: string) => [...tariffKeys.all, 'rates', versionId] as const,
  active: () => [...tariffKeys.all, 'active'] as const,
};

/**
 * Response type for paginated tariff versions list.
 * Matches the backend TariffListResponse struct.
 */
interface TariffVersionListResponse {
  data: TariffVersion[];
  total_count: number;
  limit: number;
  offset: number;
}

/**
 * Options for fetching paginated tariff versions
 */
export interface TariffVersionsPaginatedOptions {
  /** Filter by status (comma-separated for multiple, e.g., "DRAFT,ACTIVE") */
  status?: string;
  /** Search term to filter by name or description */
  search?: string;
  /** Sort by column (default: "created_at") */
  sortBy?: string;
  /** Sort order (default: "desc") */
  sortOrder?: 'asc' | 'desc';
  /** Include full rate details */
  includeRates?: boolean;
}

/**
 * Fetch paginated tariff versions from the API with filtering and sorting.
 * Uses direct fetch since the generated service doesn't include search/sort params yet.
 */
async function fetchTariffVersionsPaginated(
  limit: number,
  offset: number,
  options?: TariffVersionsPaginatedOptions
): Promise<PaginatedResponse<TariffVersion>> {
  const baseUrl = OpenAPI.BASE || '';

  // Build query parameters
  const params = new URLSearchParams({
    limit: String(limit),
    offset: String(offset),
  });

  // Add optional filters
  if (options?.status) {
    params.set('status', options.status);
  }
  if (options?.search) {
    params.set('search', options.search);
  }
  if (options?.sortBy) {
    params.set('sort_by', options.sortBy);
  }
  if (options?.sortOrder) {
    params.set('sort_order', options.sortOrder);
  }
  if (options?.includeRates) {
    params.set('include_rates', 'true');
  }

  const url = `${baseUrl}/api/v1/tariff-versions?${params.toString()}`;

  const headers: HeadersInit = {
    'Content-Type': 'application/json',
  };

  // Add auth token if available
  if (typeof OpenAPI.TOKEN === 'string') {
    headers['Authorization'] = `Bearer ${OpenAPI.TOKEN}`;
  } else if (typeof OpenAPI.TOKEN === 'function') {
    const token = await OpenAPI.TOKEN({
      method: 'GET',
      url,
      headers,
      body: undefined,
      mediaType: undefined,
      path: {},
      cookies: {},
      query: { limit, offset, ...options },
    });
    if (token) {
      headers['Authorization'] = `Bearer ${token}`;
    }
  }

  const response = await fetch(url, {
    method: 'GET',
    headers,
    credentials: 'include',
  });

  if (!response.ok) {
    throw await apiErrorFromResponse(response, url, 'Failed to fetch tariff versions');
  }

  const data: TariffVersionListResponse = await response.json();

  // Transform the response data to frontend format
  const transformedData = TariffTransformer.arrayToFrontend<TariffVersion>(data.data);

  return {
    data: transformedData,
    total: data.total_count,
    limit: data.limit,
    offset: data.offset,
  };
}

/**
 * Hook to fetch paginated tariff versions with server-side pagination, filtering, and sorting.
 *
 * @param initialPageSize - Initial page size (default: 50)
 * @param options - Filter and sort options
 * @returns Paginated query result with filter/sort-aware refetching
 */
export function useTariffVersionsPaginated(
  initialPageSize = 50,
  options?: TariffVersionsPaginatedOptions
) {
  // Include options in the query key so changes trigger refetch
  const queryKeyWithOptions = (params: { limit: number; offset: number }) => [
    ...tariffKeys.listPaginated(params),
    { status: options?.status, search: options?.search, sortBy: options?.sortBy, sortOrder: options?.sortOrder, includeRates: options?.includeRates }
  ];

  return usePaginatedQuery<TariffVersion>({
    queryKey: queryKeyWithOptions,
    queryFn: (limit, offset) => fetchTariffVersionsPaginated(limit, offset, options),
    initialPageSize,
  });
}

// Hook to fetch all tariff versions
export function useTariffs(includeRates = false, limit = 50, offset = 0) {
  const { isSignedIn } = useAuth();
  const { isAuthReady } = useAuthContext();
  
  return useQuery({
    queryKey: tariffKeys.list({ includeRates, limit, offset }),
    queryFn: async () => {
      const response = await TariffsService.getApiV1TariffVersions(
        undefined,    // groupType
        undefined,    // status
        undefined,    // isDefault
        includeRates,
        undefined,    // search
        'created_at', // sortBy
        'desc',       // sortOrder
        limit,
        offset
      );
      // Transform the response to frontend format and return just the data array
      if (response?.data) {
        const transformed = TariffTransformer.arrayToFrontend<TariffVersion>(response.data);
        return transformed;
      }
      return [];
    },
    enabled: isSignedIn && isAuthReady,
    staleTime: 5 * 60 * 1000, // 5 minutes
    retry: retryUnlessClientError,
  });
}

/**
 * Page size used when paging through tariff versions to find one by id.
 * Capped at 100 because the list endpoint rejects `limit > 100` with a 400
 * ("Limit must be between 1 and 100"); requesting more makes every page fail.
 */
const TARIFF_VERSION_PAGE_SIZE = 100;

/**
 * Fetch a single tariff version by id.
 *
 * The list endpoint exposes no by-id filter, so we page through the versions and
 * select the matching one client-side. We keep requesting pages (advancing the
 * offset) until we find the match or exhaust the list — relying on a single page
 * would silently miss versions older than the first page. Returns metadata only —
 * callers that need rates use `useTariffRates`. Exported so non-hook call sites
 * can prime/read the same `tariffKeys.detail(id)` cache entry via
 * `queryClient.ensureQueryData`.
 */
export async function fetchTariffVersionById(id: string): Promise<TariffVersion | null> {
  if (!id) return null;
  let offset = 0;
  for (;;) {
    const response = await TariffsService.getApiV1TariffVersions(
      undefined,                  // groupType
      undefined,                  // status
      undefined,                  // isDefault
      false,                      // includeRates - metadata only; rates come from useTariffRates
      undefined,                  // search
      'created_at',               // sortBy
      'desc',                     // sortOrder
      TARIFF_VERSION_PAGE_SIZE,   // limit
      offset                      // offset
    );
    const page = response?.data ?? [];
    const match = page.find((v) => v.id === id);
    if (match) {
      return TariffTransformer.toFrontend(match);
    }
    offset += page.length;
    const total = response?.total_count;
    // Stop once the API returns a short (final) page, an empty page, or we have
    // walked past the reported total — there are no further rows to search.
    if (page.length < TARIFF_VERSION_PAGE_SIZE || (typeof total === 'number' && offset >= total)) {
      return null;
    }
  }
}

// Hook to fetch a single tariff version by id
export function useTariff(id: string) {
  const { isSignedIn } = useAuth();
  const { isAuthReady } = useAuthContext();
  // `fetchTariffVersionById` pages `GET /tariff-versions`, which admits these modules only (#1691).
  const canReadTariffs = useModuleGate(TARIFF_VERSION_LIST_READ_MODULES);

  return useQuery({
    queryKey: tariffKeys.detail(id),
    queryFn: () => fetchTariffVersionById(id),
    enabled: !!id && isSignedIn && isAuthReady && canReadTariffs,
    staleTime: 5 * 60 * 1000,
  });
}

// Hook to fetch active tariff version
export function useActiveTariff() {
  const { isSignedIn } = useAuth();
  const { isAuthReady } = useAuthContext();

  return useQuery({
    queryKey: tariffKeys.active(),
    queryFn: async () => {
      const response = await TariffsService.getApiV1TariffVersions(
        undefined,    // groupType
        'ACTIVE',     // status - filter for active versions only
        undefined,    // isDefault
        true,         // includeRates
        undefined,    // search
        'created_at', // sortBy
        'desc',       // sortOrder
        1,            // limit
        0             // offset
      );
      // Transform the response to frontend format
      if (response?.data?.[0]) {
        return TariffTransformer.toFrontend(response.data[0]);
      }
      return null;
    },
    enabled: isSignedIn && isAuthReady,
    staleTime: 5 * 60 * 1000,
  });
}

// Hook to fetch tariff rates for a version
export function useTariffRates(versionId: string, options?: { enabled?: boolean }) {
  const { isSignedIn } = useAuth();
  const { isAuthReady } = useAuthContext();

  return useQuery({
    queryKey: tariffKeys.rates(versionId),
    // Annotated because the defensive unwrapping below returns from four different
    // branches; without it the query data widens and every consumer's callback
    // parameter falls back to an implicit `any`.
    queryFn: async (): Promise<billing_TariffRateResponse[]> => {
      const response = await TariffsService.getApiV1TariffVersionsTariffs(versionId);
      
      // Check if response has a nested structure like { rates: [...] }
      if (response && typeof response === 'object' && !Array.isArray(response)) {
        // Check common nested properties
        if ('rates' in response) {
          // eslint-disable-next-line @typescript-eslint/no-explicit-any
          const rates = (response as any).rates;
          return rates || [];
        }
        if ('data' in response) {
          // eslint-disable-next-line @typescript-eslint/no-explicit-any
          const data = (response as any).data;
          return data || [];
        }
        if ('tariff_rates' in response) {
          // eslint-disable-next-line @typescript-eslint/no-explicit-any
          const tariffRates = (response as any).tariff_rates;
          return tariffRates || [];
        }
      }
      
      // Return raw API response without transformation - TariffGrid expects snake_case fields
      // (service_code, day_of_week, age_type, daily_rate, is_extra_day)
      const result = response || [];
      return result;
    },
    enabled: !!versionId && isSignedIn && isAuthReady && (options?.enabled !== false),
    staleTime: 5 * 60 * 1000,
  });
}

// Hook to create a new tariff version
export function useCreateTariff() {
  const queryClient = useQueryClient();
  const { i18n } = useLingui();

  return useMutation({
    mutationFn: async (tariff: billing_CreateTariffVersionRequest) => {
      // Don't transform - the API types already use snake_case
      const response = await TariffsService.postApiV1TariffVersions(tariff);
      
      // Transform response to frontend format if needed
      const frontendResponse =
        TariffTransformer.toFrontend<billing_TariffVersionResponse>(response);
      
      return frontendResponse;
    },
    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey: tariffKeys.lists() });
      // Also set the created tariff data in the cache for immediate availability
      if (data?.id) {
        queryClient.setQueryData(tariffKeys.detail(data.id), data);
      }
      toast.success(i18n._('common.success.created'));
    },
    onError: (error: unknown) => {
      if (errorStatus(error) === 409) {
        toast.error(i18n._('tariffs.toast.overlapError'));
        return;
      }
      toast.error(errorToastText(error, i18n, i18n._('common.error.createFailed')));
    },
  });
}

// Helper function for exponential backoff retry
async function retryWithBackoff<T>(
  fn: () => Promise<T>,
  maxRetries = 3,
  initialDelay = 1000
): Promise<T> {
  let lastError: Error | null = null;

  for (let attempt = 0; attempt < maxRetries; attempt++) {
    try {
      // Try the function - if it succeeds, return immediately without delay
      const result = await fn();
      return result;
    } catch (error) {
      lastError = error as Error;

      // Don't retry on client errors (400-499)
      const status = errorStatus(error);
      if (status !== undefined && status >= 400 && status < 500) {
        throw error;
      }

      // If this is the last attempt, throw the error
      if (attempt === maxRetries - 1) {
        throw error;
      }

      // Only delay on failure, before the next retry attempt
      // Calculate delay with exponential backoff
      const delay = initialDelay * Math.pow(2, attempt);
      await new Promise(resolve => setTimeout(resolve, delay));
    }
  }

  throw lastError || new Error('Retry failed');
}

/**
 * Converts the rates the tariff grid holds (`billing_TariffRateResponse`, whose
 * `age_type` and `service_code` are optional strings) into the request shape the
 * bulk-update endpoint declares (`billing_TariffRateRequest`, whose `age_type`
 * is a nominal string enum and whose `service_code` is required).
 *
 * Every field is carried across verbatim — in particular `daily_rate` (Rappen)
 * is never defaulted, coerced or rounded. A rate the API could not accept
 * throws instead of being silently dropped or sent with an undefined key.
 */
export function toTariffRateRequests(
  rates: billing_TariffRateResponse[]
): billing_TariffRateRequest[] {
  return rates.map((rate) => {
    const ageType = toEnumMember(
      billing_TariffRateRequestEnums.age_type,
      rate.age_type
    );
    if (ageType === undefined) {
      throw new Error(`Tariff rate has an unsupported age_type: ${rate.age_type}`);
    }
    if (!rate.service_code) {
      throw new Error('Tariff rate is missing service_code');
    }
    return {
      age_type: ageType,
      service_code: rate.service_code,
      daily_rate: rate.daily_rate,
      day_of_week: rate.day_of_week,
      is_extra_day: rate.is_extra_day,
      subsidised: rate.subsidised,
    };
  });
}

// Helper to chunk rates by service code
function chunkRatesByService(rates: billing_TariffRateRequest[]): Map<string, billing_TariffRateRequest[]> {
  const chunks = new Map<string, billing_TariffRateRequest[]>();

  rates.forEach(rate => {
    const serviceCode = rate.service_code;
    if (!chunks.has(serviceCode)) {
      chunks.set(serviceCode, []);
    }
    chunks.get(serviceCode)!.push(rate);
  });

  return chunks;
}

// Transaction result type for detailed tracking
export interface TariffUpdateResult {
  attempted: number;
  succeeded: number;
  failed: Array<{
    service: string;
    /** Undefined when the failing rate carried no day_of_week (optional in the API model). */
    day: number | undefined;
    error: string;
  }>;
  transactionId?: string;
  updatedAt?: string;
}

// Hook to update tariff rates in bulk with retry logic and optional chunking
export function useUpdateTariffRates(options?: {
  enableChunking?: boolean;
  maxRetries?: number;
}) {
  const queryClient = useQueryClient();
  const { i18n } = useLingui();
  const enableChunking = options?.enableChunking ?? true; // Default to enabled
  const maxRetries = options?.maxRetries ?? 3;

  return useMutation({
    mutationFn: async ({
      versionId,
      rates,
      versionTag,
      onProgress
    }: {
      versionId: string;
      rates: billing_BulkUpdateTariffRatesRequest;
      versionTag?: string; // For optimistic locking using updated_at timestamp
      onProgress?: (current: number, total: number, service?: string) => void;
    }): Promise<TariffUpdateResult> => {
      // Initialize detailed tracking
      const result: TariffUpdateResult = {
        attempted: rates.rates?.length || 0,
        succeeded: 0,
        failed: [],
      };

      // Don't transform rates - they're already in snake_case format from TariffUnifiedGrid
      // The grid component works with snake_case fields (is_extra_day, not isExtraDay)

      // Only chunk for large payloads (>50 rates) when chunking is enabled
      if (!enableChunking || !rates.rates || rates.rates.length <= 50) {
        // Small payload or chunking disabled - send all at once with retry
        if (onProgress) {
          onProgress(0, 1, 'all');
        }

        try {
          const response = await retryWithBackoff(
            async () => {
              const payload = versionTag ? { ...rates, version_tag: versionTag } : rates;
              return await TariffsService.putApiV1TariffVersionsTariffs(versionId, payload);
            },
            maxRetries
          );

          // Extract details from response if available
          if (response && typeof response === 'object') {
            // eslint-disable-next-line @typescript-eslint/no-explicit-any
            const res = response as any;
            if (res.upserted !== undefined) {
              result.succeeded = res.upserted;
            } else {
              result.succeeded = result.attempted; // Assume all succeeded if no detail
            }
            if (res.transaction_id) {
              result.transactionId = res.transaction_id;
            }
            if (res.updated_at) {
              result.updatedAt = res.updated_at;
            }
          } else {
            result.succeeded = result.attempted;
          }

          if (onProgress) {
            onProgress(1, 1, 'all');
          }
        } catch (error) {
          result.failed.push({
            service: 'all',
            day: 0,
            error: error instanceof Error ? error.message : 'Unknown error',
          });
          throw error;
        }

        return result;
      }

      // Chunk by service code for larger payloads (>50 rates)
      const chunks = chunkRatesByService(rates.rates);
      const totalChunks = chunks.size;
      let currentChunk = 0;
      let totalSucceeded = 0;

      for (const [serviceCode, serviceRates] of chunks) {
        if (onProgress) {
          onProgress(currentChunk, totalChunks, serviceCode);
        }

        try {
          const chunkPayload: billing_BulkUpdateTariffRatesRequest = {
            rates: serviceRates
          };

          if (versionTag) {
            // eslint-disable-next-line @typescript-eslint/no-explicit-any
            (chunkPayload as any).version_tag = versionTag;
          }

          const response = await retryWithBackoff(
            async () => await TariffsService.putApiV1TariffVersionsTariffs(versionId, chunkPayload),
            maxRetries
          );

          // Extract success count from response
          if (response && typeof response === 'object') {
            // eslint-disable-next-line @typescript-eslint/no-explicit-any
            const res = response as any;
            if (res.upserted !== undefined) {
              totalSucceeded += res.upserted;
            } else {
              totalSucceeded += serviceRates.length;
            }
            // Store transaction details from last successful chunk
            if (res.transaction_id) {
              result.transactionId = res.transaction_id;
            }
            if (res.updated_at) {
              result.updatedAt = res.updated_at;
            }
          } else {
            totalSucceeded += serviceRates.length;
          }

          currentChunk++;

          if (onProgress) {
            onProgress(currentChunk, totalChunks, serviceCode);
          }

          // Minimal delay between chunks only for very large payloads
          // This prevents overwhelming the server while keeping performance fast
          if (currentChunk < totalChunks && totalChunks > 10) {
            await new Promise(resolve => setTimeout(resolve, 10));
          }
        } catch (error) {
          // Track failed chunk
          serviceRates.forEach(rate => {
            result.failed.push({
              service: rate.service_code,
              day: rate.day_of_week,
              error: error instanceof Error ? error.message : 'Unknown error',
            });
          });

          // Continue with other chunks instead of stopping
          console.error(`Failed to update rates for service ${serviceCode}:`, error);
        }
      }

      result.succeeded = totalSucceeded;

      // If some succeeded and some failed, still invalidate cache for partial success
      if (result.succeeded > 0) {
        queryClient.invalidateQueries({ queryKey: tariffKeys.rates(versionId) });
        queryClient.invalidateQueries({ queryKey: tariffKeys.detail(versionId) });
      }

      // Return the detailed result
      return result;
    },
    onSuccess: (res, variables) => {
      // Debug logging removed

      // Don't show toast messages here - let the components show more detailed messages
      // The components (TariffUnifiedGrid and TariffGrid) show better contextual messages

      // Show warning/error only for partial success or failure cases
      if (res.succeeded > 0 && res.failed.length > 0) {
        // Partial success - show warning
        toast.warning(
          i18n._({
            id: 'tariff.update.partialSuccess',
            message: 'Partially updated: {succeeded} succeeded, {failed} failed',
            values: { succeeded: res.succeeded, failed: res.failed.length }
          })
        );
      } else if (res.succeeded === 0 && res.failed.length > 0) {
        // Complete failure - show error
        toast.error(
          i18n._({
            id: 'tariff.update.completeFailure',
            message: 'Failed to update tariff rates: All {attempted} updates failed',
            values: { attempted: res.attempted }
          })
        );
      }
      // For complete success, let the component show the success message

      // Always invalidate queries to refresh data
      queryClient.invalidateQueries({ queryKey: tariffKeys.rates(variables.versionId) });
      queryClient.invalidateQueries({ queryKey: tariffKeys.detail(variables.versionId) });
    },
    onError: (error: unknown, variables) => {
      if (errorStatus(error) === 409) {
        toast.error(i18n._('tariffs.toast.versionConflict'));
        return;
      }
      logger.error('tariff rates update failed', {
        context: 'useUpdateTariffRates',
        versionId: variables.versionId,
        error,
      });
      toast.error(errorToastText(error, i18n, msg`Failed to update tariff rates`));
    },
    retry: false, // We handle retries internally
  });
}

// Hook to activate a tariff version (set as default)
export function useActivateTariff() {
  const queryClient = useQueryClient();
  const { i18n } = useLingui();

  return useMutation({
    mutationFn: async (versionId: string) => {
      const response = await TariffsService.putApiV1TariffVersionsSetDefault(versionId);
      // Transform response if it contains data
      return response ? TariffTransformer.toFrontend(response) : response;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: tariffKeys.lists() });
      queryClient.invalidateQueries({ queryKey: tariffKeys.active() });
      toast.success(i18n._('tariffs.toast.activateSuccess'));
    },
    onError: (error: unknown) => {
      console.error('Failed to activate tariff:', error);
      toast.error(errorToastText(error, i18n, msg`Failed to activate tariff`));
    },
  });
}

// Hook to update a tariff version
export function useUpdateTariff(options?: {
  /** No success toast: the caller saves as one step of a larger action and reports once. */
  silent?: boolean;
}) {
  const queryClient = useQueryClient();
  const { i18n } = useLingui();
  const silent = options?.silent ?? false;

  return useMutation({
    mutationFn: async ({ id, data }: { id: string; data: billing_UpdateTariffVersionRequest }) => {
      const response = await TariffsService.putApiV1TariffVersions(id, data);
      // Transform response to frontend format
      return response ? TariffTransformer.toFrontend(response) : response;
    },
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({ queryKey: tariffKeys.lists() });
      queryClient.invalidateQueries({ queryKey: tariffKeys.detail(variables.id) });
      if (!silent) {
        toast.success(i18n._('tariffs.toast.updateSuccess'));
      }
    },
    onError: (error: unknown) => {
      if (errorStatus(error) === 409) {
        toast.error(i18n._('tariffs.toast.overlapError'));
        return;
      }
      toast.error(errorToastText(error, i18n, i18n._('common.error.updateFailed')));
    },
  });
}

// Hook to archive a tariff version
export function useArchiveTariff() {
  const queryClient = useQueryClient();
  const { i18n } = useLingui();

  return useMutation({
    mutationFn: async (versionId: string) => {
      // Update the tariff status to 'ARCHIVED'
      const updateData: billing_UpdateTariffVersionRequest = {
        status: billing_UpdateTariffVersionRequest.status.ARCHIVED
      };
      
      const response = await TariffsService.putApiV1TariffVersions(versionId, updateData);
      // Transform response to frontend format
      return response ? TariffTransformer.toFrontend(response) : response;
    },
    onSuccess: (_, versionId) => {
      queryClient.invalidateQueries({ queryKey: tariffKeys.lists() });
      queryClient.invalidateQueries({ queryKey: tariffKeys.detail(versionId) });
      queryClient.invalidateQueries({ queryKey: tariffKeys.active() });
      toast.success(i18n._('tariffs.toast.archiveSuccess'));
    },
    onError: (error: unknown) => {
      toast.error(errorToastText(error, i18n, i18n._('tariffs.toast.archiveFailed')));
    },
  });
}

// Hook to create or update a single tariff rate
export function useCreateOrUpdateTariffRate() {
  const queryClient = useQueryClient();
  const { i18n } = useLingui();

  return useMutation({
    mutationFn: async ({ versionId, rate }: { versionId: string; rate: billing_TariffRateRequest }) => {
      // Transform rate to backend format
      const backendRate = TariffTransformer.toBackend<billing_TariffRateRequest>(rate);
      const response = await TariffsService.postApiV1TariffVersionsTariffs(versionId, backendRate);
      // Transform response to frontend format
      return response ? TariffTransformer.toFrontend(response) : response;
    },
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({ queryKey: tariffKeys.rates(variables.versionId) });
      queryClient.invalidateQueries({ queryKey: tariffKeys.detail(variables.versionId) });
      toast.success(i18n._('tariffs.toast.updateRateSuccess'));
    },
    onError: (error: Error) => {
      console.error('Failed to update tariff rate:', error);
      toast.error(i18n._(msg`Failed to update tariff rate`));
    },
  });
}

// Helper hook to get formatted tariff data for display
export function useTariffWithRates(versionId: string) {
  const tariffQuery = useTariff(versionId);
  const ratesQuery = useTariffRates(versionId);
  
  const rates = ratesQuery.data || [];

  return {
    tariff: tariffQuery.data,
    rates: rates,
    isLoading: tariffQuery.isLoading || ratesQuery.isLoading,
    error: tariffQuery.error || ratesQuery.error,
  };
}
